import React from 'react';
import styles from './TitleBar.module.scss';
import { usePanelContainer } from '../Panels/usePanelContainer';
import { useTool } from '@hooks/Tool/useTool';
import { TOOL_REGISTRY } from "config/toolRegistry";
import { useAnalysisPage } from '@features/analysis/useAnalysisPage';
import { ICON_MAP, TOOL_ICON_MAP } from '@components/Icons/Icons';
import GroupContextMenu from '@components/ContextMenu/GroupContextMenu';
import BaseContextMenu from '@components/ContextMenu/BaseContextMenu';
import { scheduleRender } from '../Panels/renderState';
import Button from '@components/Button';
import NumberPagination from '@components/NumberPagination/NumberPagination';
import { MouseTooltip } from '@components/Tooltip';
import { useEjectionFractionModal } from '@features/analysis/analysisModal/EjectionFractionModal/EjectionFractionModal';
import useModalActions from '@hooks/common/useModalAction';
import { useBrowserPage } from '@features/browser/useBrowserPage';
import { useSegmentManager } from '@features/analysis/segmentManager';
import { useConfig } from 'config/useConfig';
import {
  LESION_STACK_SPACING_MIN_MM,
  LESION_STACK_SPACING_MAX_MM,
  LESION_STACK_THICKNESS_MIN_MM,
  LESION_STACK_THICKNESS_MAX_MM,
  normalizeLesionStackSpacingMm,
  normalizeLesionStackThicknessMm,
} from 'config/config';
import { update2DLabel, useBufferPool } from '../Panels/Renderers/vtkManagers';
import { getIsSegmentedRegionSelected } from '@features/analysis/analysisList/hooks/useEditModeConfig';
import { getMouseTooltipTriggerProps, useTextTooltip } from '@components/Tooltip/useMouseTooltip';
import { getConfiguredCprCrossSectionImageMode } from '../Panels/cprImageModeConfig';
import { getCprCrossSectionImageManifest } from '../Panels/cprImageModeState';

export interface TitleBarProps {}

export const TitleBar: React.FC<TitleBarProps> = () => {
  const DropDownIcon = ICON_MAP['dropdown'];
  const {
    panelToolState,
    globalToolState,
    layoutConfig,
    selectedLayoutConfig,
    selectedPanel,
    updateCurrentLayout
  } = useAnalysisPage();
  const {
    analysisType,
    selectedPanelComponentKey
  } = usePanelContainer();
  const {
    selectedSeries,
    selectedSeriesId,
    selectedStudySeries,
    seriesSelections,
    titleInfo
  } = useBrowserPage();
  const {
    createToolContext,
    handleToolActivate,
    getToolDefinition,
    getVisibleToolsForContext,
    getGroupedToolsForMenu,
  } = useTool();
  const { config,setData } = useConfig();
  const { bufferPool } = useBufferPool();
  const selectedSeriesPool = selectedSeriesId ? bufferPool[selectedSeriesId] : undefined;
  const {mode:manualMode,dirty} = useSegmentManager();
  const groupedTools = getGroupedToolsForMenu();
  const [showAnnotationMenu, setShowAnnotationMenu] = React.useState(false);
  const [showMoreToolMenu, setShowMoreToolMenu] = React.useState(false);
  const [showLesionStackMenu, setShowLesionStackMenu] = React.useState(false);
  const isAnnotationsVisible =
    globalToolState?.toggles?.ToggleAnnotations ?? true;

  const isPatientVisible =
    globalToolState?.toggles?.TogglePatientData ?? true;


  const navigationTools = React.useMemo(
    () => TOOL_REGISTRY.filter(t => t.group === "navigation"),
    []
  );

  const isSegmentedRegionSelected = getIsSegmentedRegionSelected();

  const visibleTools = getVisibleToolsForContext();
  const visibleToolIds = visibleTools.map(t => t.id);
  const invisibleToolIds = React.useMemo(() => {
    return TOOL_REGISTRY.map(t => t.id).filter(id => !visibleToolIds.includes(id));
  }, [visibleToolIds]);

  const mergedTools = navigationTools.map(tool => ({
    ...tool,
    enabled: visibleToolIds.includes(tool.id),
  }));

  const annotationGroups = React.useMemo(() => {
    if (!groupedTools?.annotation) return [];

    return [
      {
        groupName: "Annotations",
        toggleEnabled: false,
        items: groupedTools.annotation.map(tool => ({
          label: tool.name,
          onClick: () => handleToolActivate(tool.id),
          isChecked: tool.behavior === 'toggle' ? (tool.id === 'ToggleDicomPanSync' ? (globalToolState?.toggles?.[tool.id] ?? true) : (globalToolState?.toggles?.[tool.id] ?? false)) : undefined,
          isDisabled: !visibleToolIds.includes(tool.id),
        })),
      },
    ];
  }, [groupedTools, handleToolActivate, globalToolState, visibleToolIds]);

  const includedGroupsForMoreTools = ['measurement', 'utility'] as const;
  const excludedToolIds = [
    'ToggleSegmentationManager',
    'ResetView',
    'FitToScreen',
    'OneOnOneResolution',
    'ToggleHeartCut',
    'ToggleMIP',
    'Ventricle',
    'CoronaryAorta',
    'ColorPreset_Type1',
    'ColorPreset_Type2',
    'ColorPreset_Type3',
  ] as const;
  const moreToolsGroups = React.useMemo(() => {
    if (!groupedTools) return [];
    return Object.entries(groupedTools)
      .filter(([groupName]) =>
        includedGroupsForMoreTools.includes(groupName as any),
      )
      .map(([groupName, tools]) => ({
        groupName: groupName.charAt(0).toUpperCase() + groupName.slice(1),
        toggleEnabled: false,
        items: tools
          .filter((tool) => !excludedToolIds.includes(tool.id as any))
          .map((tool) => ({
            label: tool.name,
            onClick: () => {

              const nextToolId =
                tool.group === 'measurement' &&
                panelToolState?.activeToolId === tool.id
                  ? ['VR','Surface3D'].includes(selectedPanelComponentKey!)
                    ? 'Rotation'
                    : 'Scroll'
                  : tool.id;

              handleToolActivate(nextToolId);
            },
            isDisabled: !visibleToolIds.includes(tool.id),
            isChecked:
              tool.group === 'measurement' &&
              panelToolState?.activeToolId === tool.id
                ? true
                : tool.behavior === 'toggle'
                  ? tool.id === 'ToggleDicomPanSync'
                    ? (globalToolState?.toggles?.[tool.id] ?? true)
                    : (globalToolState?.toggles?.[tool.id] ?? false)
                  : undefined,
          })),
      }))
      .filter((group) => group.items.length > 0);
  }, [groupedTools, handleToolActivate, globalToolState, visibleToolIds]);
  const isMoreToolsAvailable = moreToolsGroups.length > 0 && !manualMode;

  // About Ejection Fraction
  const EjectionFractionModalOpen = useEjectionFractionModal();
  const [state, setState] = React.useState<number>(-1);
  const { errorDefaultAction } = useModalActions();

  const labels = selectedStudySeries.map((series) => {
    const selection = seriesSelections.find((sel) => sel.id === series.id);
    return selection ? `${selection.percent}%` : series.id.slice(-4);
  });

  const disabledIndexes = selectedStudySeries
    .map((series, idx) =>
      seriesSelections.find((sel) => sel.id === series.id) ? -1 : idx,
    )
    .filter((idx) => idx >= 0);

  const handleEFSeriesChange = React.useCallback(
    (index: number, init?: boolean) => {
      requestAnimationFrame(() => {
        scheduleRender('Axial', { type: 'UpdateEF', index });
        scheduleRender('Coronal', { type: 'UpdateEF', index });
        scheduleRender('Sagittal', { type: 'UpdateEF', index });
        scheduleRender('VR', { type: 'UpdateEF', index });
      });
    },
    [],
  );

  React.useEffect(() => {
    if (seriesSelections.length === 0) return;
    if (analysisType !== 'ejectionFraction') return;
    const minSelection = seriesSelections.reduce((min, cur) => {
      return Number(cur.percent) < Number(min.percent) ? cur : min;
    });

    const minIdx = selectedStudySeries.findIndex(
      (series) => series.id === minSelection.id,
    );

    if (minIdx !== -1) {
      console.log('저는 쐈어요...', {
        minIdx,
        selectedStudySeries,
        minSelection,
        efSeriesId: selectedStudySeries[minIdx]?.id,
      });
      handleEFSeriesChange(minIdx, true);
      setState(minIdx + 1);
    }
  }, [analysisType, seriesSelections, selectedStudySeries]);


  React.useEffect(() => {
    if(!selectedPanel) return;
    if(analysisType !== 'chamber' &&  ['DICOMPanel', 'CrossSectionPanel', 'CPRPanel'].includes(selectedPanel)){
      if(panelToolState?.activeToolId === "Rotation"){
        handleToolActivate('Scroll');
        console.log('강제로 바꿈 Rotation-> Scroll.')
      }
    }
  }, [analysisType, selectedPanel]);

  // Capture 관련
  const CameraIcon = ICON_MAP['camera'];

  const captureClick = async () => {
    console.log('쏴라2')
    scheduleRender('panel',{type:'fullscreencapture'});
  };

  //ffr 관련
  const OutlineIcon = ICON_MAP['outline'];
  const EraserIcon = ICON_MAP['eraser'];
  const PrimaryIcon = ICON_MAP['primary_FFR'];
  const getFFRStyleTooltipText = (
    tool: 'fill' | 'outline' | 'primary',
  ) => {
    const text = {
      fill: 'Show outline only',
      outline: 'Show filled area',
      primary: 'Toggle Segmented Region',
    }[tool];

    return `${text}`;
  };

  const { tooltipNode, tooltipHandlers } = useTextTooltip();

  // About Layout
  const layoutTypes = layoutConfig.map((layout) => layout.type);
  const layoutIcons = layoutTypes.map((type) => {
    const key = `layout_${type!.replace(/-/g, '')}`;
    return {
      type,
      icon: TOOL_ICON_MAP[key] || null,
    };
  });

  //FFR은 capture용,edit용으로 레이아웃 만들어서 사용하는데 아이콘으로 연결은 안해도댐
 const layoutIconsVisible= analysisType !== 'FFR';
  const handleLayoutChange = (type: string) => {
    updateCurrentLayout(type);
    if (analysisType !== 'ejectionFraction') return;

    const currentEFIndex = state - 1;
    if (currentEFIndex < 0 || currentEFIndex >= selectedStudySeries.length) {
      return;
    }

    handleEFSeriesChange(currentEFIndex);
  };

  const cprCrossSectionImageMode = getConfiguredCprCrossSectionImageMode();
  const cprCrossSectionImageManifest = getCprCrossSectionImageManifest(
    cprCrossSectionImageMode,
    selectedSeriesPool,
  );
  const cprCrossSectionImageRevision = cprCrossSectionImageManifest?.revision;
  const hasPlaqueFrontDataManifest =
    cprCrossSectionImageMode === 'server' &&
    typeof cprCrossSectionImageRevision === 'string' &&
    cprCrossSectionImageRevision.startsWith('front-data:') &&
    cprCrossSectionImageRevision.split(':').includes('plaque');
  const isLesionStackFeatureAvailable =
    analysisType === 'plaque' && hasPlaqueFrontDataManifest;
  const isLesionStackViewLayout =
    analysisType === 'plaque' && selectedLayoutConfig.type === 'lesionStackView';

  React.useEffect(() => {
    if (!isLesionStackViewLayout || isLesionStackFeatureAvailable) return;
    setShowLesionStackMenu(false);
    updateCurrentLayout('5-2-2');
  }, [
    isLesionStackFeatureAvailable,
    isLesionStackViewLayout,
    updateCurrentLayout,
  ]);
  const lesionStackSpacingMm = normalizeLesionStackSpacingMm(
    config.lesionStackSpacingMm,
  );
  const lesionStackThicknessMm = normalizeLesionStackThicknessMm(
    config.lesionStackThicknessMm,
  );
  const [lesionStackSpacingDraft, setLesionStackSpacingDraft] = React.useState(
    () => lesionStackSpacingMm.toFixed(1),
  );
  const [lesionStackThicknessDraft, setLesionStackThicknessDraft] = React.useState(
    () => lesionStackThicknessMm.toFixed(1),
  );
  const [isEditingLesionStackSpacing, setIsEditingLesionStackSpacing] =
    React.useState(false);
  const [isEditingLesionStackThickness, setIsEditingLesionStackThickness] =
    React.useState(false);

  React.useEffect(() => {
    if (isEditingLesionStackSpacing) return;
    setLesionStackSpacingDraft(lesionStackSpacingMm.toFixed(1));
  }, [isEditingLesionStackSpacing, lesionStackSpacingMm]);

  React.useEffect(() => {
    if (isEditingLesionStackThickness) return;
    setLesionStackThicknessDraft(lesionStackThicknessMm.toFixed(1));
  }, [isEditingLesionStackThickness, lesionStackThicknessMm]);

  const isLesionStackDraftValueAllowed = React.useCallback(
    (value: string, max: number) => {
      if (value === '') return true;
      if (!/^\d*(?:\.\d{0,1})?$/.test(value)) return false;
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return true;
      return numeric <= max;
    },
    [],
  );

  const handleLesionStackInputKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      event.stopPropagation();
      if (event.key === 'Enter') {
        event.currentTarget.blur();
      }
    },
    [],
  );

  const handleLesionStackLayoutChange = React.useCallback(() => {
    if (manualMode) return;
    updateCurrentLayout(isLesionStackViewLayout ? '5-2-2' : 'lesionStackView');
  }, [isLesionStackViewLayout, manualMode, updateCurrentLayout]);

  const handleLesionStackSpacingChange = React.useCallback(
    (value: string) => {
      if (!isLesionStackDraftValueAllowed(value, LESION_STACK_SPACING_MAX_MM)) {
        return;
      }
      setLesionStackSpacingDraft(value);
      const numeric = Number(value);
      if (value === '' || !Number.isFinite(numeric)) return;
      setData({ lesionStackSpacingMm: normalizeLesionStackSpacingMm(numeric) });
    },
    [isLesionStackDraftValueAllowed, setData],
  );

  const handleLesionStackThicknessChange = React.useCallback(
    (value: string) => {
      if (!isLesionStackDraftValueAllowed(value, LESION_STACK_THICKNESS_MAX_MM)) {
        return;
      }
      setLesionStackThicknessDraft(value);
      const numeric = Number(value);
      if (value === '' || !Number.isFinite(numeric)) return;
      setData({ lesionStackThicknessMm: normalizeLesionStackThicknessMm(numeric) });
    },
    [isLesionStackDraftValueAllowed, setData],
  );

  const handleLesionStackSpacingBlur = React.useCallback(() => {
    setIsEditingLesionStackSpacing(false);
    const normalized = normalizeLesionStackSpacingMm(lesionStackSpacingDraft);
    setData({ lesionStackSpacingMm: normalized });
    setLesionStackSpacingDraft(normalized.toFixed(1));
  }, [lesionStackSpacingDraft, setData]);

  const handleLesionStackThicknessBlur = React.useCallback(() => {
    setIsEditingLesionStackThickness(false);
    const normalized = normalizeLesionStackThicknessMm(lesionStackThicknessDraft);
    setData({ lesionStackThicknessMm: normalized });
    setLesionStackThicknessDraft(normalized.toFixed(1));
  }, [lesionStackThicknessDraft, setData]);

  const RefreshIcon = ICON_MAP['reset'];

  function FFRIconButton({
    icon,
    tooltip,
    onClick,
    className = '',
    shortcut,
  }: {
    icon: React.ReactNode;
    tooltip: string;
    onClick: () => void;
    className?: string;
    shortcut?:string;
  }) {
    const handlePointerDown = React.useCallback(
      (e: React.PointerEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      },
      [onClick],
    );

    return (
      <div
        className={`${styles['icon-FFR']} ${className}`}
        onPointerDown={handlePointerDown}
        {...getMouseTooltipTriggerProps(tooltip, tooltipHandlers,shortcut)}
      >
        {icon}
      </div>
    );
  }
  const ffrButtons = React.useMemo<
    {
      icon: React.ReactNode;
      tooltip: string;
      onClick: () => void;
      className?: string;
    }[]
  >(() => {
    if (config.EditMode) {
      const nextMode =
        config.manualLabelmapStyle === 'outline' ? 'fill' : 'outline';

      return [
        {
          icon: <OutlineIcon />,
          tooltip: getFFRStyleTooltipText(config.manualLabelmapStyle),
          shortcut:'O',
          onClick: () => {
            scheduleRender('Surface3D', {
              type: 'FFR_Manual_3D',
              mode: nextMode,
            });
          },
          className:
            config.manualLabelmapStyle === 'fill' ? styles.fillActive : '',
        },
      ];
    }

    return [
      {
        icon: <PrimaryIcon />,
        tooltip: getFFRStyleTooltipText('primary'),
        shortcut:'S',
        onClick: update2DLabel,
        className: isSegmentedRegionSelected ? styles.selected : '',
      },
      {
        icon: <EraserIcon />,
        tooltip: 'Erase all FFR',
        onClick: () => {
          scheduleRender('Surface3D', {
            type: 'eraserPointFFR',
          });
        },
      },
    ];
  }, [
    config.EditMode,
    config.manualLabelmapStyle,
    isSegmentedRegionSelected,
    OutlineIcon,
    PrimaryIcon,
    EraserIcon,
  ]);

  // console.log('titlebar lock',manualMode,dirty)
  return (
    <div
      className={styles.header}
      onClick={(e) => {
        if (manualMode) {
          e.stopPropagation();
          e.preventDefault();
        }
      }}
    >
      {/* <div className={styles.container}> */}
      <div className={styles.toolOptions}>
        <div className={styles.left}>
          <div
            className={`${styles.buttonBox} ${!selectedSeriesId ? styles.disabled : ''}`}
            onClick={() => handleToolActivate('ResetView')}

          >
            <span className={styles.label}> {'ResetView'} </span>
            {/* <RefreshIcon /> */}
          </div>
        </div>
        {mergedTools.map((tool) => {
          const isCprModificationMode =
            selectedPanelComponentKey === 'CPR' &&
            config.cprModificationTool !== 'none';

          const isActive =
            panelToolState?.activeToolId === tool.id && !isCprModificationMode;
          const isDisabled = invisibleToolIds.includes(tool.id);

          return (
            <div
              key={tool.id}
              onClick={() => {
                if (isDisabled) return;
                setData({ cprModificationTool: 'none' });
                handleToolActivate(tool.id);
              }}
              className={`${styles.buttonBox} ${isActive ? styles.active : ''} ${isDisabled ? styles.disabled : ''}`}
            >
              <span className={styles.label}>
                {' '}
                {tool.name === 'Window / Level' ? 'W/L' : tool.name}{' '}
              </span>
            </div>
          );
        })}
        <div
          data-context-menu-trigger="true"
          className={`${styles.buttonBox} ${selectedSeries ? '' : styles.disabled}`}
          onClick={() => {
            setShowMoreToolMenu(false);
            setShowLesionStackMenu(false);
            setShowAnnotationMenu((prev) => !prev);
          }}
        >
          <span className={styles.label}> {'Annotation'} </span>
          <span className={styles.RightIcon}>
            <DropDownIcon />
          </span>
        </div>
        <div
          data-context-menu-trigger="true"
          className={`${styles.buttonBox} ${!isMoreToolsAvailable ? styles.disabled : ''} ${analysisType === 'empty' ? styles.disabled : ''}`}
          onClick={() => {
            setShowAnnotationMenu(false);
            setShowLesionStackMenu(false);
            setShowMoreToolMenu((prev) => !prev);
          }}
        >
          <span className={styles.label}> {'More Tools'} </span>
          <span className={styles.RightIcon}>
            <DropDownIcon />
          </span>
        </div>
        {isLesionStackFeatureAvailable && (
          <div
            data-context-menu-trigger="true"
            className={`${styles.buttonBox} ${manualMode ? styles.disabled : ''} ${isLesionStackViewLayout ? styles.active : ''}`}
            onClick={() => {
              if (manualMode) return;
              setShowAnnotationMenu(false);
              setShowMoreToolMenu(false);
              setShowLesionStackMenu((prev) => !prev);
            }}
          >
            <span className={styles.label}> {'Lesion Stack'} </span>
            <span className={styles.RightIcon}>
              <DropDownIcon />
            </span>
          </div>
        )}
        {analysisType === 'FFR' &&
          ffrButtons.map((button, index) => (
            <FFRIconButton key={`${button.tooltip}-${index}`} {...button} />
          ))}
      </div>

      <>
        {analysisType !== 'ejectionFraction' ? (
          isPatientVisible ? (
            <span className={styles.title}>{titleInfo}</span>
          ) : null
        ) : selectedStudySeries.length === 1 ? (
          <></>
        ) : (
          <div className={styles['titlebar__ejection3']}>
            <Button onClick={() => EjectionFractionModalOpen(selectedSeries!)}>
              Recalculate
            </Button>
            <NumberPagination
              value={state}
              setValue={setState}
              total={selectedStudySeries.length}
              labels={labels}
              disabledIndexes={disabledIndexes}
              onIndexChange={handleEFSeriesChange}
            />
          </div>
        )}
      </>
      <div className={styles.toolOptions}>
        {layoutIconsVisible &&
          layoutIcons.length > 1 &&
          layoutIcons.map(({ icon: Icon, type }, idx) =>
            Icon ? (
              <div key={idx} className={styles['icon-20']}>
                <div
                  className={`${styles.select} ${manualMode ? styles.disabled : ''}`}
                  onClick={() => handleLayoutChange(type!)}
                >
                  {Icon}
                </div>
              </div>
            ) : null,
          )}

        <div className={styles['icon-20']}>
          <CameraIcon onClick={captureClick} />
        </div>
      </div>

      {showAnnotationMenu && (
        <GroupContextMenu
          groups={annotationGroups}
          onClose={() => setShowAnnotationMenu(false)}
        />
      )}
      {showMoreToolMenu && (
        <GroupContextMenu
          groups={moreToolsGroups}
          onClose={() => setShowMoreToolMenu(false)}
        />
      )}
      {showLesionStackMenu && isLesionStackFeatureAvailable && (
        <BaseContextMenu onClose={() => setShowLesionStackMenu(false)}>
          <div
            className={styles.lesionStackMenu}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.lesionStackMenuTitle}>Lesion Stack</div>
            <button
              type="button"
              className={`${styles.lesionStackToggleButton} ${
                isLesionStackViewLayout ? styles.active : ''
              }`}
              onClick={handleLesionStackLayoutChange}
              disabled={manualMode}
            >
              <span>View Mode</span>
              <strong>{isLesionStackViewLayout ? 'On' : 'Off'}</strong>
            </button>
            <div className={styles.lesionStackControlRow}>
              <label className={styles.lesionStackControlLabel}>Spacing</label>
              <input
                className={styles.lesionStackControlInput}
                type="text"
                inputMode="decimal"
                autoComplete="off"
                aria-label="Lesion Stack spacing millimeters"
                data-min={LESION_STACK_SPACING_MIN_MM}
                data-max={LESION_STACK_SPACING_MAX_MM}
                value={lesionStackSpacingDraft}
                disabled={!isLesionStackViewLayout || manualMode}
                onFocus={() => setIsEditingLesionStackSpacing(true)}
                onChange={(e) => handleLesionStackSpacingChange(e.target.value)}
                onKeyDownCapture={handleLesionStackInputKeyDown}
                onKeyDown={handleLesionStackInputKeyDown}
                onBlur={handleLesionStackSpacingBlur}
              />
              <span className={styles.lesionStackControlUnit}>mm</span>
            </div>
            <div className={styles.lesionStackControlRow}>
              <label className={styles.lesionStackControlLabel}>
                Thickness
              </label>
              <input
                className={styles.lesionStackControlInput}
                type="text"
                inputMode="decimal"
                autoComplete="off"
                aria-label="Lesion Stack thickness millimeters"
                data-min={LESION_STACK_THICKNESS_MIN_MM}
                data-max={LESION_STACK_THICKNESS_MAX_MM}
                value={lesionStackThicknessDraft}
                disabled={!isLesionStackViewLayout || manualMode}
                onFocus={() => setIsEditingLesionStackThickness(true)}
                onChange={(e) =>
                  handleLesionStackThicknessChange(e.target.value)
                }
                onKeyDownCapture={handleLesionStackInputKeyDown}
                onKeyDown={handleLesionStackInputKeyDown}
                onBlur={handleLesionStackThicknessBlur}
              />
              <span className={styles.lesionStackControlUnit}>mm</span>
            </div>
          </div>
        </BaseContextMenu>
      )}
      {tooltipNode}
      {/* </div> */}
    </div>
  );
};
